import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Gauge, Sparkles, Upload, Wand2, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import GeneratorForm from "../components/GeneratorForm";
import ResourceBuilder from "../components/ResourceBuilder";
import SupplyPlanForm from "../components/SupplyPlanForm";
import LessonDocument from "../components/LessonDocument";
import UnitDocument from "../components/UnitDocument";
import ActivityResultCard from "../components/ActivityResultCard";
import LessonInProgressBanner from "../components/LessonInProgressBanner";
import SkillsGeneratorForm from "../components/SkillsGeneratorForm";
import FreeLessonForm from "../components/FreeLessonForm";
import PostGenScheduleDialog from "../components/PostGenScheduleDialog";
// Pre-launch banner removed Feb 2026 launch.
import {
  generateActivity,
  generateFromResource,
  generateSummativeTask,
  generateSupplyPlan,
  getActivity,
  resumeGenerateJob,
  resumeStagedUnitBuild,
  startStagedUnitBuild,
  previewSkillsBreakdown,
} from "../lib/api";
import {
  clearPendingGenerate,
  clearPendingStagedBuild,
  getPendingGenerate,
  getPendingStagedBuild,
} from "../lib/inProgress";
import StagedBuildProgress from "../components/StagedBuildProgress";
import CoveragePlanCard from "../components/CoveragePlanCard";
import { AlertTriangle as AlertTri, CalendarClock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { track } from "../lib/analytics";

/**
 * /generator — the standalone tool page.
 *
 * Top section: the same Main Generator that used to live on the home
 * page (Generate Activity / Build from Resource / Plan for a Supply
 * modes) with the Skill in the Wild sidebar.
 *
 * Bottom section: the Skills Generator — a focused skill-first variant
 * with a Primary + Secondary transferable skill picker.
 */
// Phase 104 — Generator Timeout UI Fix. Network blips / 5-minute
// poll timeouts while the lesson is still generating server-side
// should NOT surface as an error toast — the slow-state banner
// already calmly explains the situation. Real errors (validation,
// paywall, server 5xx with a detail message) still toast normally.
const isTimeoutLikeError = (err) => {
  if (!err) return false;
  if (err.code === "ECONNABORTED") return true;
  const msg = String(err.message || err.toString() || "").toLowerCase();
  if (msg.includes("timed out")) return true;
  if (msg.includes("timeout")) return true;
  if (msg.includes("network error")) return true;
  if (err?.response?.status === 504) return true;
  return false;
};

export const GeneratorPage = () => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase 104 — generator timeout UI: track when the current job started
  // so the in-progress banner can flip to the calm "this is taking a
  // little longer" copy + "Go to My Library →" CTA after 45 seconds.
  const [generationStartedAt, setGenerationStartedAt] = useState(null);
  const [coverageBump, setCoverageBump] = useState(0);
  const [resumingJob, setResumingJob] = useState(null);
  // Fork Relocation (Stage B) — staged unit build wait state.
  const [stagedActive, setStagedActive] = useState(false);
  const [stagedProgress, setStagedProgress] = useState("");
  const [stagedMeta, setStagedMeta] = useState(null); // {startedAt, estimateSeconds, model, numLessons}
  const [stagedBlocked, setStagedBlocked] = useState(null); // {reason, form}
  const [coveragePlan, setCoveragePlan] = useState(null); // {taskActivityId, breakdown, summary, model, form, resultRefArg}
  const [planLoading, setPlanLoading] = useState(false);
  const [planProgress, setPlanProgress] = useState("");
  // Fingerprint of the current plan-relevant inputs (grade/subject/strand/
  // expectations/big idea/model/lessons). Lets us discard a Coverage Plan that
  // was planned against selections the teacher has since changed.
  const planFpRef = useRef("");
  const handlePlanInputsChange = useCallback((fp) => {
    planFpRef.current = fp;
    // If a plan is already shown and the inputs no longer match it, drop it —
    // never let a stale plan sit above changed selections.
    setCoveragePlan((cp) => (cp && cp.fp && cp.fp !== fp ? null : cp));
  }, []);
  const [builderMode, setBuilderMode] = useState("form");
  // Phase 140 — Three-Mode Generator Switcher. The /generator page
  // now presents the three existing lesson generators behind a single
  // segmented control: Simplified · Full · Skill-Focused. Default on
  // every load is "full" (per spec — simplicity should be a chosen
  // mode, not the landing one). Each mode is independent: switching
  // tabs simply shows that generator and hides the other two. There
  // is NO shared state across modes — grade/subject/skill never
  // carry from one mode to another.
  const [lessonMode, setLessonMode] = useState("full");
  // Tiny analytics ping so we can see how teachers actually move
  // between the three tabs over the first few weeks. Only fires on a
  // real switch (no-op when the same tab is clicked again). Payload
  // is structured metadata only — no prompt text, no lesson content.
  const switchLessonMode = (next) => {
    // Simplified was collapsed into the full generator (Jun 2026 — "Free
    // runs Full"). Any lingering deep link / caller asking for it lands
    // on Full so nothing 404s or dead-ends.
    const target = next === "simplified" ? "full" : next;
    setLessonMode((prev) => {
      if (prev !== target) {
        track("lesson_mode_switched", { from: prev, to: target });
      }
      return target;
    });
  };
  const location = useLocation();

  // Phase 138.F — when the lesson workspace deep-links us via
  // `/generator?mode=resource&followup_from=<id>&grade=…&subject=…&skill_focus=…`
  // we switch to the Build-from-resource pane and pre-fill the confirm
  // form so the teacher can build a follow-up in one extra tap (just
  // re-upload the file). We never stored the source, so no auto-upload.
  const followupPrefill = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    if ((params.get("mode") || "") !== "resource") return null;
    const followupFrom = params.get("followup_from") || "";
    if (!followupFrom) return null;
    return {
      followup_from: followupFrom,
      grade: params.get("grade") || "",
      subject: params.get("subject") || "",
      skill_focus: params.get("skill_focus") || "",
    };
  }, [location.search]);

  useEffect(() => {
    if (followupPrefill) {
      // Follow-up deep links always land in the Full generator's
      // Build-from-resource sub-mode, never the Simplified/Skill tabs.
      setLessonMode("full");
      setBuilderMode("resource");
    }
  }, [followupPrefill]);
  // Tracks the activity id we've already prompted-to-schedule, so the
  // post-generation dialog doesn't re-pop on re-renders.
  const [postgenSeen, setPostgenSeen] = useState(null);
  const resultRef = useRef(null);
  const skillsResultRef = useRef(null);
  const simplifiedResultRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    setCoverageBump((n) => n + 1);
  }, [user?.user_id]);

  // Resume any generation that was in flight before tab refresh/close.
  useEffect(() => {
    const pending = getPendingGenerate();
    if (!pending?.jobId) return undefined;
    let cancelled = false;
    (async () => {
      // Hoist the "we're resuming" UI state inside the async IIFE so
      // React-19's set-state-in-effect rule doesn't fire on the
      // synchronous body of the effect itself.
      if (cancelled) return;
      setResumingJob(pending);
      setLoading(true);
      toast.info("Picking up where you left off…");
      let suppressedTimeout = false;
      try {
        const data = await resumeGenerateJob(pending.jobId);
        if (cancelled) return;
        setActivity(data);
        setCoverageBump((n) => n + 1);
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          clearPendingGenerate();
        } else if (isTimeoutLikeError(err)) {
          // Phase 104 — keep the slow-state banner up; the lesson will
          // land in My Library when it's ready. No error toast.
          suppressedTimeout = true;
        } else {
          toast.error("Could not finish your earlier lesson");
        }
      } finally {
        if (!cancelled && !suppressedTimeout) {
          setResumingJob(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resume a staged unit build that was in flight before tab refresh/close.
  // The build completes server-side and its inputs live on the job doc, so
  // the teacher never re-enters Step 1 / Step 2 — we just re-attach to poll.
  useEffect(() => {
    const pending = getPendingStagedBuild();
    if (!pending?.jobId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setStagedActive(true);
      setLoading(true);
      setStagedMeta({
        startedAt: pending.startedAt || Date.now(),
        estimateSeconds: pending.estimateSeconds || 95,
        model: pending.model,
        numLessons: pending.numLessons,
      });
      toast.info("Picking up your unit build where you left off…");
      let suppressedTimeout = false;
      try {
        const job = await resumeStagedUnitBuild(pending.jobId, {
          onProgress: (msg) => {
            if (!cancelled) setStagedProgress(msg);
          },
        });
        if (cancelled) return;
        await finishStagedBuild(job);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          clearPendingStagedBuild();
          setStagedActive(false);
        } else if (isTimeoutLikeError(err)) {
          suppressedTimeout = true;
        } else {
          setStagedActive(false);
          toast.error("Could not finish your unit build");
        }
      } finally {
        if (!cancelled && !suppressedTimeout) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill skill focus from a `?skill=` URL param (used by Skills
  // Library "Generate a lesson" + Skill in the Wild "Create activity"
  // CTAs). GeneratorForm picks it up via sessionStorage on mount/focus.
  // Phase 124 — also handles `?skill_of_day=1` from the home page
  // quick-action card: we fetch today's Skill in the Wild and use it
  // as the prefill so the teacher lands with the skill already chosen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const skill = params.get("skill");
    if (skill) {
      try {
        sessionStorage.setItem("atomicedu_prefill_skill_focus", skill);
      } catch (_e) {
        /* sessionStorage blocked — fine */
      }
      window.dispatchEvent(new Event("focus"));
      return;
    }
    if (params.get("skill_of_day") === "1") {
      // Lazy import so we don't drag the SITW API into every Generator
      // page load when the param isn't present.
      import("../lib/api").then(({ api }) => {
        api
          .get("/skill-in-the-wild")
          .then((r) => {
            // The SITW endpoint returns `skill_library_name` (display
            // name used by Skills Library + Generator's skill-focus
            // dropdown). `skill_canonical_name` is the slug fallback.
            const todaySkill =
              r?.data?.skill_library_name || r?.data?.skill_canonical_name;
            if (todaySkill) {
              try {
                sessionStorage.setItem("atomicedu_prefill_skill_focus", todaySkill);
              } catch (_e) {
                /* blocked — fine */
              }
              window.dispatchEvent(new Event("focus"));
            }
          })
          .catch(() => {
            /* SITW fetch failed (free tier or network) — fall through
               so the teacher still lands on the Generator with no
               prefill. */
          });
      });
    }
  }, []);

  const finishStagedBuild = async (job, resultRefArg = resultRef) => {
    if (job?.blocked) {
      // Blocked-task refusal routes to "decide at the end": we surface
      // the honest reason and offer to build the unit without a task.
      setStagedBlocked({
        reason: job.block_reason || "We couldn't design a rich culminating task from these expectations.",
      });
      setStagedActive(false);
      setLoading(false);
      return;
    }
    const unitId = job?.unit_activity_id;
    if (!unitId) throw new Error("Build finished without a unit id");
    const unit = await getActivity(unitId);
    setActivity(unit);
    setCoverageBump((n) => n + 1);
    setStagedActive(false);
    setStagedProgress("");
    setStagedMeta(null);
    track("staged_unit_built", {
      model: job?.model || "",
      task_ms: job?.timings?.task_ms || 0,
      lessons_ms: job?.timings?.lessons_ms || 0,
      total_ms: job?.timings?.total_ms || 0,
    });
    toast.success("Your task-aligned unit is ready", {
      description: `${unit.activity_name} — auto-saved to your library.`,
    });
    setTimeout(() => {
      resultRefArg.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const runStagedBuild = async (form, resultRefArg = resultRef) => {
    // Stage 1.5 — surface the Coverage Plan BEFORE the heavy build. The
    // pre-call generates the culminating task + skills breakdown; the build
    // then REUSES both (no double work). Default-accept: one tap to proceed.
    setStagedBlocked(null);
    setCoveragePlan(null);
    setPlanLoading(true);
    setPlanProgress("");
    setLoading(true);
    setActivity(null);
    const pendingFp = planFpRef.current; // snapshot of inputs at pre-call time
    setTimeout(() => {
      resultRefArg.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    try {
      const res = await previewSkillsBreakdown(form, { onProgress: setPlanProgress });
      // Stale guard — if the teacher changed a strand/expectation/big idea while
      // the ~60s pre-call was running, this plan is against the OLD selections.
      // Discard it silently rather than show it against the new form.
      if (planFpRef.current !== pendingFp) {
        setLoading(false);
        return;
      }
      if (res?.blocked) {
        setStagedBlocked({ reason: res.block_reason || "We couldn't design a rich culminating task from these expectations." });
        setLoading(false);
        return;
      }
      if (res?.no_breakdown || !res?.breakdown) {
        // No task-derived plan for this path → build as today.
        return executeStagedBuild(form, resultRefArg);
      }
      setCoveragePlan({
        taskActivityId: res.task_activity_id,
        breakdown: res.breakdown,
        summary: res.summary,
        model: form.model,
        form,
        resultRefArg,
        fp: pendingFp,
      });
      setLoading(false); // card is shown; re-enable the form until they tap Generate
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        // Plan still running server-side — rare; fall back to a direct build.
        return executeStagedBuild(form, resultRefArg);
      }
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Could not plan the unit", { description: String(detail) });
      setLoading(false);
    } finally {
      setPlanLoading(false);
    }
  };

  const generateFromCoveragePlan = () => {
    if (!coveragePlan) return undefined;
    const { form, taskActivityId, breakdown, resultRefArg } = coveragePlan;
    setCoveragePlan(null);
    return executeStagedBuild(
      { ...form, task_activity_id: taskActivityId, skills_breakdown_id: breakdown?.id },
      resultRefArg || resultRef,
    );
  };

  const executeStagedBuild = async (form, resultRefArg = resultRef) => {
    setStagedBlocked(null);
    setStagedActive(true);
    setStagedProgress("");
    setLoading(true);
    setActivity(null);
    const startedAt = Date.now();
    setStagedMeta({
      startedAt,
      estimateSeconds: 95,
      model: form.model,
      numLessons: form.num_lessons,
    });
    setTimeout(() => {
      resultRefArg.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    let suppressedTimeout = false;
    try {
      const job = await startStagedUnitBuild(form, {
        onProgress: (msg) => setStagedProgress(msg),
        onJobStarted: (_jobId, estimate) => {
          if (estimate) {
            setStagedMeta((m) => ({ ...(m || {}), estimateSeconds: estimate }));
          }
        },
      });
      await finishStagedBuild(job, resultRefArg);
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        // Build still running server-side; it'll land in My Library.
        // Keep the wait card up (poll resumes on remount).
        suppressedTimeout = true;
        return;
      }
      setStagedActive(false);
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Could not build the unit", { description: String(detail) });
    } finally {
      if (!suppressedTimeout) setLoading(false);
    }
  };

  const handleGenerate = async (form, resultRefArg = resultRef) => {
    // Fork Relocation (Stage B) — staged unit build (task → task-aware
    // lessons) rides its own resilient job + wait card.
    if (form?.output_type === "staged_unit") {
      return runStagedBuild(form, resultRefArg);
    }
    setLoading(true);
    setGenerationStartedAt(Date.now());
    setActivity(null);
    let suppressedTimeout = false;
    try {
      // Phase 85a — summative tasks hit a dedicated endpoint so the
      // backend can enforce the Standard-tier gate + 2+ expectation rule.
      const isSummative = form?.output_type === "summative_task";
      const data = isSummative
        ? await generateSummativeTask(form)
        : await generateActivity(form);
      setActivity(data);
      setCoverageBump((n) => n + 1);
      // Analytics — fire ONE event per output type. Payload is structured
      // metadata only: never include prompt text, lesson content, or
      // teacher freeform notes.
      track(
        isSummative
          ? "summative_generated"
          : form?.output_type === "unit"
          ? "unit_generated"
          : "lesson_generated",
        {
          grade: form?.grade || "",
          subject: form?.subject || "",
          subjects_count: Array.isArray(form?.subjects) ? form.subjects.length : 1,
          strands_count: Array.isArray(form?.strands_detail)
            ? form.strands_detail.length
            : 0,
          expectations_count: Array.isArray(form?.expectations_detail)
            ? form.expectations_detail.length
            : 0,
          is_cross_curricular: Array.isArray(form?.subjects) && form.subjects.length >= 2,
          num_lessons: form?.num_lessons || undefined,
          activity_type: form?.activity_type || "",
          has_skill_focus: !!form?.skill_focus,
          share_to_community: !!form?.share_to_community,
        }
      );
      toast.success(isSummative ? "Summative task ready" : "Activity ready", {
        description: `${data.activity_name} — auto-saved to your library.`,
      });
      setTimeout(() => {
        resultRefArg.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        // Lesson is still cooking server-side and will land in the
        // library on completion — leave the slow-state banner up,
        // suppress the error toast (per Phase 104 spec).
        suppressedTimeout = true;
        return;
      }
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Could not generate activity", { description: String(detail) });
    } finally {
      if (!suppressedTimeout) {
        setLoading(false);
        setGenerationStartedAt(null);
      }
    }
  };

  const handleGenerateFromResource = async (payload) => {
    setLoading(true);
    setGenerationStartedAt(Date.now());
    setActivity(null);
    let suppressedTimeout = false;
    try {
      const data = await generateFromResource(payload);
      setActivity(data);
      setCoverageBump((n) => n + 1);
      toast.success("Lesson built from your resource", {
        description: `${data.activity_name} — auto-saved to your library.`,
      });
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        suppressedTimeout = true;
        return;
      }
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Could not build from resource", { description: String(detail) });
    } finally {
      if (!suppressedTimeout) {
        setLoading(false);
        setGenerationStartedAt(null);
      }
    }
  };

  const handleGenerateSupply = async (prompt) => {
    if (!prompt || !prompt.trim()) {
      toast.error("Tell us what the supply teacher needs to cover today.");
      return;
    }
    setLoading(true);
    setGenerationStartedAt(Date.now());
    setActivity(null);
    let suppressedTimeout = false;
    try {
      const data = await generateSupplyPlan({ prompt });
      setActivity(data);
      setCoverageBump((n) => n + 1);
      toast.success("Supply plan ready", {
        description: `${data.activity_name} — auto-saved to your library.`,
      });
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      if (isTimeoutLikeError(err)) {
        suppressedTimeout = true;
        return;
      }
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error("Could not build supply plan", { description: String(detail) });
    } finally {
      if (!suppressedTimeout) {
        setLoading(false);
        setGenerationStartedAt(null);
      }
    }
  };

  return (
    <>
    <div
      className="bg-[#f4f5f7] text-[#4a6984]"
      data-testid="generator-page"
    >
      {/* MAIN GENERATOR */}
      <section
        data-testid="generator-section"
        className="pt-6 pb-12 md:pt-10 md:pb-16"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-6">
            <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
              The Generator
            </p>
            <h1 className="mt-3 font-black text-3xl sm:text-4xl text-[#4a6984] tracking-tight">
              Tell us the conditions. We&apos;ll design the activity.
            </h1>
          </div>

          {/* Phase 140 — Three-Mode Generator Switcher. Segmented
              control across the top: Simplified · Full · Skill-Focused
              (simplest left). Default = Full on every load. Tabs are
              all visible and tappable on mobile (no overflow menu). */}
          <div
            className="mb-6"
            data-testid="lesson-mode-row"
          >
            <div
              role="tablist"
              aria-label="Lesson generator mode"
              className="inline-flex flex-wrap gap-1 p-1 rounded-xl border border-[#4a6984]/15 bg-white shadow-sm"
              data-testid="lesson-mode-switcher"
            >
              <button
                type="button"
                role="tab"
                aria-selected={lessonMode === "full"}
                onClick={() => switchLessonMode("full")}
                data-testid="lesson-mode-full"
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-extrabold tracking-tight transition-all ${
                  lessonMode === "full"
                    ? "bg-[#1c2d3d] text-white shadow-sm"
                    : "text-[#4a6984] hover:bg-[#f4f5f7]"
                }`}
              >
                <Gauge className="h-4 w-4" /> Full
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={lessonMode === "skill"}
                onClick={() => switchLessonMode("skill")}
                data-testid="lesson-mode-skill"
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-extrabold tracking-tight transition-all ${
                  lessonMode === "skill"
                    ? "bg-[#3EB489] text-white shadow-sm"
                    : "text-[#4a6984] hover:bg-[#3EB489]/10"
                }`}
              >
                <Wand2 className="h-4 w-4" />
                <span className="sm:hidden">Skills</span>
                <span className="hidden sm:inline">Skill-Focused</span>
              </button>
            </div>
            <p
              data-testid="lesson-mode-descriptor"
              className="mt-3 text-sm font-semibold text-[#4a6984]/80"
            >
              {lessonMode === "skill"
                ? "Skill-Focused: built around one transferable skill — no subject required."
                : "Full: the complete lesson package."}
            </p>
          </div>

          {lessonMode === "skill" && (
            <div
              data-testid="lesson-mode-skill-pane"
              className="grid grid-cols-1 gap-8"
            >
              <div className="lg:max-w-4xl">
                <SkillsGeneratorForm
                  onGenerate={(f) => handleGenerate(f, skillsResultRef)}
                  loading={loading}
                />
                <div ref={skillsResultRef} className="mt-10 space-y-6">
                  {loading && !activity && (
                    <LessonInProgressBanner
                      variant="generate"
                      startedAt={generationStartedAt}
                    />
                  )}
                  {activity && (
                    activity.output_type === "unit" || activity.unit_markdown ? (
                      <UnitDocument
                        activity={activity}
                        onActivityUpdate={(updated) => {
                          setActivity(updated);
                          setCoverageBump((n) => n + 1);
                        }}
                      />
                    ) : (activity.lesson_markdown || activity.summative_markdown) ? (
                      <LessonDocument
                        activity={activity}
                        onActivityUpdate={(updated) => {
                          setActivity(updated);
                          setCoverageBump((n) => n + 1);
                        }}
                      />
                    ) : (
                      <ActivityResultCard activity={activity} />
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {lessonMode === "full" && (
          <div
            data-testid="lesson-mode-full-pane"
            className="grid grid-cols-1 gap-8"
          >
            <div>
              <div
                className="mb-6 flex flex-wrap items-center justify-between gap-3"
                data-testid="builder-mode-row"
              >
                <div
                  className="inline-flex flex-wrap p-1 rounded-xl border border-[#4a6984]/15 bg-white shadow-sm max-w-full"
                  data-testid="builder-mode-toggle"
                >
                  <button
                    type="button"
                    onClick={() => setBuilderMode("form")}
                    data-testid="builder-mode-form"
                    aria-pressed={builderMode === "form"}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-extrabold tracking-tight transition-all ${
                      builderMode === "form"
                        ? "bg-[#1c2d3d] text-white"
                        : "text-[#4a6984] hover:bg-[#f4f5f7]"
                    }`}
                  >
                    <Sparkles className="h-4 w-4" /> Generate activity
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuilderMode("resource")}
                    data-testid="builder-mode-resource"
                    aria-pressed={builderMode === "resource"}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-extrabold tracking-tight transition-all ${
                      builderMode === "resource"
                        ? "bg-[#1c2d3d] text-white"
                        : "text-[#4a6984] hover:bg-[#f4f5f7]"
                    }`}
                  >
                    <Upload className="h-4 w-4" /> Build from resource
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuilderMode("supply")}
                    data-testid="builder-mode-supply"
                    aria-pressed={builderMode === "supply"}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-extrabold tracking-tight transition-all ${
                      builderMode === "supply"
                        ? "bg-[#1c2d3d] text-white"
                        : "text-[#4a6984] hover:bg-[#f4f5f7]"
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4" /> Plan for a Supply
                  </button>
                </div>
              </div>

              {builderMode === "supply" ? (
                <SupplyPlanForm
                  onGenerate={handleGenerateSupply}
                  loading={loading}
                />
              ) : builderMode === "resource" ? (
                <ResourceBuilder
                  onGenerate={handleGenerateFromResource}
                  loading={loading}
                  followupPrefill={followupPrefill}
                />
              ) : (
                <GeneratorForm
                  onGenerate={(f) => handleGenerate(f, resultRef)}
                  loading={loading}
                  onPlanInputsChange={handlePlanInputsChange}
                />
              )}
              <div ref={resultRef} className="mt-10 space-y-6">
                {planLoading && !coveragePlan && (
                  <div
                    className="rounded-2xl border-2 border-[#256e64]/20 bg-white p-6 shadow-sm"
                    data-testid="coverage-plan-loading"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 shrink-0 grid place-items-center rounded-lg bg-[#256e64]/12 text-[#256e64] animate-pulse">
                        <Sparkles className="h-4 w-4" />
                      </span>
                      <div>
                        <h3 className="font-black text-base text-[#1c2d3d]">Planning your unit…</h3>
                        <p className="mt-0.5 text-sm text-[#4a6984]">
                          {planProgress || "Mapping each expectation to the lessons that will teach it — about a minute."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {coveragePlan && !stagedActive && (
                  <CoveragePlanCard
                    summary={coveragePlan.summary}
                    breakdown={coveragePlan.breakdown}
                    model={coveragePlan.model}
                    generating={false}
                    onGenerate={generateFromCoveragePlan}
                    onCancel={() => {
                      setCoveragePlan(null);
                      setLoading(false);
                    }}
                  />
                )}
                {stagedActive && (
                  <StagedBuildProgress
                    progress={stagedProgress}
                    estimateSeconds={stagedMeta?.estimateSeconds || 95}
                    startedAt={stagedMeta?.startedAt}
                    numLessons={stagedMeta?.numLessons}
                    model={stagedMeta?.model || "standalone"}
                  />
                )}
                {stagedBlocked && !stagedActive && (
                  <div
                    className="rounded-2xl border-2 border-[#b45309]/30 bg-[#fffbeb] p-6"
                    data-testid="staged-build-blocked"
                  >
                    <div className="flex items-start gap-3">
                      <span className="h-9 w-9 shrink-0 grid place-items-center rounded-lg bg-[#b45309]/15 text-[#b45309]">
                        <AlertTri className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-black text-base text-[#1c2d3d]">
                          A culminating task didn&apos;t fit these expectations
                        </h3>
                        <p className="mt-1 text-sm text-[#4a6984]" data-testid="staged-build-blocked-reason">
                          {stagedBlocked.reason}
                        </p>
                        <p className="mt-2 text-sm text-[#4a6984]/80">
                          You can build the unit now and choose a Test or task at
                          the end instead.
                        </p>
                        <button
                          type="button"
                          data-testid="staged-build-blocked-dismiss"
                          onClick={() => setStagedBlocked(null)}
                          className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#1c2d3d] text-white px-4 py-2 text-sm font-bold hover:bg-[#0e1a25]"
                        >
                          <CalendarClock className="h-4 w-4" />
                          Got it — I&apos;ll pick "Decide at the end"
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {(loading || resumingJob) && !activity && !stagedActive && !stagedBlocked && (
                  <LessonInProgressBanner
                    variant="generate"
                    startedAt={resumingJob?.startedAt || generationStartedAt}
                  />
                )}
                {activity && (
                  activity.output_type === "unit" || activity.unit_markdown ? (
                    <UnitDocument
                      activity={activity}
                      onActivityUpdate={(updated) => {
                        setActivity(updated);
                        setCoverageBump((n) => n + 1);
                      }}
                    />
                  ) : (activity.lesson_markdown || activity.summative_markdown) ? (
                    <LessonDocument
                      activity={activity}
                      onActivityUpdate={(updated) => {
                        setActivity(updated);
                        setCoverageBump((n) => n + 1);
                      }}
                    />
                  ) : (
                    <ActivityResultCard activity={activity} />
                  )
                )}
              </div>
            </div>
            {/* Phase 115.1 — standalone Skill in the Wild card removed
                from the generator page UI. It now lives on Home as a
                daily-rotating standalone card. Generated lessons still
                include their own per-lesson SITW card in the lesson
                output (unchanged). */}
          </div>
          )}
        </div>
      </section>
    </div>
    {activity && activity.id !== postgenSeen && (
      <PostGenScheduleDialog
        activity={activity}
        onDismiss={() => setPostgenSeen(activity.id)}
      />
    )}
    </>
  );
};

export default GeneratorPage;
